import { motion } from "framer-motion";
import { TrendingUp, TrendingDown, Activity } from "lucide-react";
import { LineChart, Line, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { indexCards, fundPerformance } from "../lib/data";

function sparkData(seed) {
  let s = seed;
  const arr = [];
  let v = 50;
  for (let i = 0; i < 24; i++) {
    s = (s * 9301 + 49297) % 233280;
    v += (s / 233280 - 0.45) * 8;
    arr.push({ i, v });
  }
  return arr;
}

export default function LiveMarket() {
  return (
    <section id="markets" className="relative py-28 px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
          <div className="max-w-2xl">
            <span className="section-label inline-flex items-center gap-2">
              <Activity className="w-3 h-3" />
              Live Markets
            </span>
            <h2 className="text-4xl sm:text-5xl font-black tracking-tighter mt-5">
              India&apos;s pulse, <span className="gradient-text">at a glance</span>
            </h2>
            <p className="mt-4 text-slate-400">
              Real-time-style snapshots of indices and top mutual funds curated for clarity.
            </p>
          </div>
          <div className="flex items-center gap-2 px-4 py-2 glass rounded-full">
            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
            <span className="text-xs uppercase tracking-widest text-slate-300 font-semibold">Market Open</span>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-10">
          {indexCards.map((c, i) => {
            const up = c.change >= 0;
            const spark = sparkData(i + 7);
            return (
              <motion.div
                key={c.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                className="glass rounded-2xl p-6 card-glow transition-all"
                data-testid={`index-card-${i}`}
              >
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <div className="text-xs uppercase tracking-widest text-slate-500">{c.name}</div>
                    <div className="text-2xl font-black mt-1 font-mono">{c.value.toLocaleString("en-IN")}</div>
                  </div>
                  <div className={`p-2 rounded-lg ${up ? "bg-emerald-500/10" : "bg-red-500/10"}`}>
                    {up ? <TrendingUp className="w-4 h-4 text-emerald-400" /> : <TrendingDown className="w-4 h-4 text-red-400" />}
                  </div>
                </div>
                <div className={`text-sm font-bold font-mono ${up ? "text-emerald-400" : "text-red-400"}`}>
                  {up ? "+" : ""}{c.change.toFixed(2)} ({up ? "+" : ""}{c.pct.toFixed(2)}%)
                </div>
                <div className="mt-3 h-12">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={spark}>
                      <Line type="monotone" dataKey="v" stroke={up ? "#10B981" : "#EF4444"} strokeWidth={2} dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </motion.div>
            );
          })}
        </div>

        <div className="grid lg:grid-cols-5 gap-6">
          <div className="lg:col-span-3 glass-strong rounded-2xl p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold">Top Mutual Fund Performance</h3>
              <span className="text-xs text-slate-500">1-Year Returns (%)</span>
            </div>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={fundPerformance} layout="vertical" margin={{ left: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1E293B" />
                <XAxis type="number" stroke="#475569" fontSize={11} />
                <YAxis type="category" dataKey="name" stroke="#94A3B8" fontSize={11} width={150} />
                <Tooltip
                  contentStyle={{ background: "#0A0F24", border: "1px solid #1E293B", borderRadius: 12 }}
                  formatter={(v) => `${v}%`}
                />
                <Bar dataKey="returns_1y" fill="#60A5FA" radius={[0, 6, 6, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="lg:col-span-2 glass-strong rounded-2xl p-6">
            <h3 className="text-lg font-bold mb-4">Trending Updates</h3>
            <div className="space-y-4">
              {[
                { tag: "FII Flow", txt: "FIIs net buyers ₹1,842 Cr in cash market today.", c: "text-emerald-400" },
                { tag: "Sector", txt: "IT sector outperforms with +1.4% gain led by Infosys.", c: "text-blue-400" },
                { tag: "RBI", txt: "Repo rate held at 6.25% — neutral stance maintained.", c: "text-amber-400" },
                { tag: "MF Inflow", txt: "Equity MF Jan inflows hit ₹52,800 Cr — record high.", c: "text-emerald-400" },
              ].map((u, i) => (
                <div key={i} className="flex gap-3 pb-3 border-b border-white/5 last:border-0">
                  <span className={`text-[10px] uppercase tracking-widest font-bold mt-0.5 ${u.c}`}>{u.tag}</span>
                  <p className="text-sm text-slate-300 flex-1">{u.txt}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
